import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSession } from "../lib/session";
import {
  getRound, getTeeSheet, teeSheetRows, myTeeTime,
  setTeeSheet, drawTeeTimes, clearTeeTimes, postTeeTimes, moveToGroup, listEntries,
  addTeeGroup, removeTeeGroup, removeFromDraw, setGroupTime,
} from "../lib/api";
import { PageHead, Loading, Notice, Empty, Field } from "../components/ui";
import Back from "../components/Back";

/**
 * The draw for a round.
 *
 * A member's own time comes first and large. Nobody should scan thirteen
 * groups for their own name, and "what time am I out?" is the only
 * question most people open this for.
 *
 * An organiser gets the same page with the controls on top: first tee,
 * interval, group size, then draw or place by hand. Posting is separate
 * and deliberate — a draw built over a week shouldn't notify the society
 * at every shuffle.
 */

const hhmm = (t) => (t ?? "").slice(0, 5);

export default function TeeTimes() {
  const { id: cid, roundId: id } = useParams();
  const { isOrganiser, profile } = useSession();

  const [round, setRound] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [rows, setRows] = useState([]);
  const [mine, setMine] = useState(null);
  const [entries, setEntries] = useState([]);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  /**
   * Moving someone: tap them, then tap the group they should be in.
   *
   * Not drag and drop. On a phone that means dragging a name across a
   * list that's already scrolling, and it's unreachable by keyboard.
   * Pick-then-place is two taps, works anywhere on the sheet, and can be
   * cancelled without consequence.
   */
  const [moving, setMoving] = useState(null);   // { profile_id, full_name }

  const [firstTee, setFirstTee] = useState("08:30");
  const [interval, setIntervalMin] = useState(8);
  const [size, setSize] = useState(4);
  const [order, setOrder] = useState("random");

  const load = async () => {
    try {
      const [r, s, g, m, e] = await Promise.all([
        getRound(id), getTeeSheet(id), teeSheetRows(id),
        myTeeTime(id).catch(() => null), listEntries(id).catch(() => []),
      ]);
      setRound(r); setSheet(s); setRows(g); setMine(m); setEntries(e);
      if (s) { setFirstTee(hhmm(s.first_tee)); setIntervalMin(s.interval_min); setSize(s.group_size); }
      setReady(true);
    } catch (e) { setError(e.message); setReady(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  /* the flat view becomes groups */
  const groups = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      if (!m.has(r.group_number)) {
        m.set(r.group_number, { id: r.group_id, number: r.group_number,
                                at: r.tee_at, manual: r.time_is_manual, players: [] });
      }
      if (r.profile_id) m.get(r.group_number).players.push(r);
    });
    return [...m.values()].sort((a, b) => a.number - b.number);
  }, [rows]);

  const drawn = new Set(rows.filter((r) => r.profile_id).map((r) => r.profile_id));
  const undrawn = entries.filter((e) => !drawn.has(e.profile_id));

  const run = async (fn, msg) => {
    setError(""); setNote(""); setBusy(true);
    try { await fn(); await load(); if (msg) setNote(msg); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (!ready) return <Loading />;
  if (!round) return (
    <>
      <Back to={`/c/${cid}/rounds`}>All rounds</Back>
      <Notice kind="error">That round isn't available.</Notice>
    </>
  );

  const posted = !!sheet?.posted_at;

  return (
    <>
      <Back to={`/c/${cid}/r/${id}`}>{round.name}</Back>
      <PageHead title="Tee times"
                sub={[round.courses?.name,
                      round.played_on && new Date(round.played_on).toLocaleDateString(
                        "en-GB", { weekday: "long", day: "numeric", month: "long" }),
                      groups.length > 0 && `${groups.length} group${groups.length === 1 ? "" : "s"}`]
                     .filter(Boolean).join(" · ")} />

      <Notice kind="error">{error}</Notice>
      <Notice kind="ok">{note}</Notice>

      {/* your own time, which is what most people came for */}
      {mine && posted && (
        <div className="mytee">
          <span className="mytee-at">
            <b>{hhmm(mine.tee_at)}</b>
            <small>You</small>
          </span>
          <span className="mytee-who">
            <strong>{mine.playing_with ? `with ${mine.playing_with}` : "on your own"}</strong>
            <small>First tee {hhmm(mine.first_tee)}</small>
          </span>
        </div>
      )}

      {!posted && !isOrganiser && (
        <Empty title="Not posted yet">
          Your organiser hasn't published the draw for this round. It'll
          appear here, and on the society's notices, as soon as they do.
        </Empty>
      )}

      {/* ---------- organiser controls ---------- */}
      {isOrganiser && (
        <div className="surface" style={{ padding: "1.1rem", marginBottom: "1.1rem" }}>
          <div className="row" style={{ marginBottom: ".8rem" }}>
            <span className="eyebrow">The draw</span>
            <span className="spacer" />
            <span className={`pill ${posted ? "pill-mark" : "pill-quiet"}`}>
              {posted ? "posted" : "draft"}
            </span>
          </div>

          <div className="grid3">
            <Field label="First tee">
              <input className="input" type="time" value={firstTee}
                     onChange={(e) => setFirstTee(e.target.value)} />
            </Field>
            <Field label="Interval">
              <select className="select" value={interval}
                      onChange={(e) => setIntervalMin(e.target.value)}>
                {[7, 8, 9, 10, 12].map((n) => <option key={n} value={n}>{n} min</option>)}
              </select>
            </Field>
            <Field label="Group">
              <select className="select" value={size} onChange={(e) => setSize(e.target.value)}>
                {[2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} ball</option>)}
              </select>
            </Field>
          </div>

          <Field label="Draw order"
                 hint="Score orders use the standings so far. In the first round nobody has one, so it falls back to handicap.">
            <select className="select" value={order} onChange={(e) => setOrder(e.target.value)}>
              <option value="random">Random</option>
              <option value="handicap">Highest handicap first</option>
              <optgroup label="By the standings — best player out last">
                <option value="nett_best_last">Nett</option>
                <option value="gross_best_last">Gross</option>
                <option value="points_best_last">Stableford</option>
              </optgroup>
              <optgroup label="By the standings — best player out first">
                <option value="nett_best_first">Nett</option>
                <option value="gross_best_first">Gross</option>
                <option value="points_best_first">Stableford</option>
              </optgroup>
            </select>
          </Field>

          <div className="row" style={{ gap: ".5rem", flexWrap: "wrap" }}>
            <button className="btn btn-ghost" disabled={busy}
                    onClick={() => run(() => setTeeSheet(id, { firstTee, interval, size }),
                                       "Times updated.")}>
              Save settings
            </button>
            <button className="btn btn-primary" disabled={busy || undrawn.length === 0}
                    onClick={() => run(async () => {
                      await setTeeSheet(id, { firstTee, interval, size });
                      const n = await drawTeeTimes(id, order);
                      setNote(`${n} player${n === 1 ? "" : "s"} drawn.`);
                    })}>
              {undrawn.length === 0 ? "Everyone is drawn"
                : `Draw the remaining ${undrawn.length}`}
            </button>
            {groups.length > 0 && (
              <button className="btn-link" style={{ color: "var(--dim)" }} disabled={busy}
                      onClick={() => {
                        if (!window.confirm("Clear the whole draw and start again?")) return;
                        run(() => clearTeeTimes(id), "Draw cleared.");
                      }}>
                Start again
              </button>
            )}
          </div>

          <p className="muted" style={{ fontSize: ".75rem", marginTop: ".7rem", lineHeight: 1.6 }}>
            Drawing places whoever isn't already in a group, so anyone you've
            put somewhere by hand stays put. Nothing is visible to members
            until you post it.
          </p>
        </div>
      )}

      {/* ---------- the sheet ---------- */}
      {moving && (
        <div className="moving-bar">
          <span>
            Moving <strong>{moving.full_name}</strong> — tap a group below
          </span>
          <span className="spacer" />
          {drawn.has(moving.profile_id) && (
            <button className="btn-link" onClick={() => run(async () => {
              await removeFromDraw(id, moving.profile_id);
              setMoving(null);
            })}>Take out</button>
          )}
          <button className="btn-link" onClick={() => setMoving(null)}>Cancel</button>
        </div>
      )}

      {groups.length === 0 ? (
        isOrganiser && <Empty title="No draw yet"
                              action={
                                <button className="btn btn-ghost" disabled={busy}
                                        onClick={() => run(() => addTeeGroup(id))}>
                                  Add a group by hand
                                </button>}>
          Set the first tee and the interval, then draw — or build the sheet
          yourself a group at a time.
        </Empty>
      ) : (
        <div className="sheet">
          {groups.map((g) => {
            const isMine = g.players.some((p) => p.profile_id === profile?.id);
            const spaces = (sheet?.group_size ?? 4) - g.players.length;
            const Row = moving && isOrganiser ? "button" : "div";
            return (
              <Row
                key={g.number}
                className={`sheet-row${isMine ? " me" : ""}${moving && isOrganiser ? " droppable" : ""}`}
                {...(moving && isOrganiser
                  ? { onClick: () => run(async () => {
                        await moveToGroup(g.id, moving.profile_id);
                        setMoving(null);
                      }) }
                  : {})}
              >
                <span className="sheet-at">
                  {hhmm(g.at)}
                  {g.manual && <small title="set by hand">·</small>}
                </span>

                <span className="sheet-players">
                  {g.players.map((p) => (
                    isOrganiser && !moving ? (
                      <button key={p.profile_id} className="sheet-name"
                              onClick={() => setMoving(p)}>
                        <span className={p.profile_id === profile?.id ? "you" : undefined}>
                          {p.full_name}
                        </span>
                        <span className="sheet-grip" aria-hidden="true" />
                      </button>
                    ) : (
                      <span key={p.profile_id}
                            className={p.profile_id === profile?.id ? "you" : undefined}>
                        {p.full_name}
                      </span>
                    )
                  ))}
                  {spaces > 0 && isOrganiser && (
                    <small className="muted">{spaces} space{spaces === 1 ? "" : "s"}</small>
                  )}
                </span>

                {isOrganiser && !moving && (
                  <span className="sheet-acts">
                    <button className="btn-link" onClick={() => {
                      const at = window.prompt(`Tee time for group ${g.number}`, hhmm(g.at));
                      if (!at || !/^\d{1,2}:\d{2}$/.test(at)) return;
                      run(() => setGroupTime(g.id, at),
                          "Time set by hand — this group won't shift with the interval.");
                    }}>Time</button>
                    <button className="btn-link" style={{ color: "var(--dim)" }}
                            onClick={() => {
                              if (g.players.length && !window.confirm(
                                `Remove this group? Its ${g.players.length} player` +
                                `${g.players.length === 1 ? "" : "s"} go back to "not drawn yet".`)) return;
                              run(() => removeTeeGroup(g.id));
                            }}>Remove</button>
                  </span>
                )}
              </Row>
            );
          })}
        </div>
      )}

      {isOrganiser && groups.length > 0 && !moving && (
        <button className="btn btn-ghost btn-block" disabled={busy}
                style={{ marginTop: ".6rem" }}
                onClick={() => run(() => addTeeGroup(id))}>
          Add another group
        </button>
      )}

      {/* anyone entered but not placed */}
      {isOrganiser && undrawn.length > 0 && (
        <div className="surface" style={{ padding: "1rem 1.1rem", marginTop: "1rem" }}>
          <span className="eyebrow">Not drawn yet · {undrawn.length}</span>
          <div className="chips" style={{ marginTop: ".55rem" }}>
            {undrawn.map((e) => (
              <button className={`chip chip-pick${
                        moving?.profile_id === e.profile_id ? " on" : ""}`}
                      key={e.profile_id}
                      onClick={() => setMoving(
                        moving?.profile_id === e.profile_id
                          ? null
                          : { profile_id: e.profile_id, full_name: e.profiles?.full_name })}>
                {e.profiles?.full_name}
              </button>
            ))}
          </div>
          {groups.length > 0 && (
            <p className="muted" style={{ fontSize: ".75rem", marginTop: ".6rem", lineHeight: 1.6 }}>
              Drawing again adds them to the last group if it has room, then
              makes new ones.
            </p>
          )}
        </div>
      )}

      {/* posting is its own decision */}
      {isOrganiser && groups.length > 0 && (
        <div className="row" style={{ gap: ".5rem", marginTop: "1.1rem", flexWrap: "wrap" }}>
          <button className="btn btn-primary" disabled={busy}
                  onClick={() => run(() => postTeeTimes(id),
                    posted ? "Re-posted — the notice has been updated."
                           : "Posted. A pinned notice is now on the society's home page.")}>
            {busy ? "Posting…" : posted ? "Post the changes" : "Post to the society"}
          </button>
          {posted && (
            <span className="muted" style={{ fontSize: ".75rem" }}>
              Posted {new Date(sheet.posted_at).toLocaleDateString("en-GB",
                { day: "numeric", month: "short" })}
            </span>
          )}
        </div>
      )}
    </>
  );
}
